import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import { FadeInOnScroll } from '../components/shared/Parallax';
import { Link } from 'react-router-dom';
import { 
  CheckCircle,
  ArrowRight,
  Calendar,
  Euro,
  Percent,
  Clock,
  Shield,
  Sparkles,
  Phone,
  Mail,
  ChevronRight,
  Sun,
  Home,
  Building,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const SmartPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('service', 'Finanziamento Tasso Zero - Giovanni Tende Smart');
      submitData.append('message', formData.message);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote`, {
        method: 'POST',
        body: submitData
      });

      if (response.ok) {
        toast.success('Richiesta inviata! Ti contatteremo presto.');
        setFormData({ name: '', phone: '', email: '', message: '' });
      } else {
        toast.error('Errore nell\'invio. Riprova.');
      }
    } catch (error) {
      toast.error('Errore di connessione. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const products = [
    { name: "Tende da Sole", icon: Sun, link: "/tende-da-sole" },
    { name: "Pergotende", icon: Home, link: "/pergotende" },
    { name: "Pergole Bioclimatiche", icon: Building, link: "/bioclimatiche" },
    { name: "Vetrate Panoramiche", icon: Building, link: "/vetrate-panoramiche" },
  ];

  return (
    <Layout>
      {/* Hero Section with Background Image */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_8f97a621-59c2-46f0-8bec-942072242924/artifacts/xh4x3n5a_bioclima%20sardegna%20tavolara.png)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeInOnScroll>
              <div>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 rounded-full mb-8 shadow-lg">
                  <Sparkles className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-semibold">Offerta valida fino al 31/12/2026</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6 leading-tight">
                  Finanziamento a
                  <span className="block text-sky-400 font-bold">Tasso Zero</span>
                </h1>
                
                <p className="text-xl text-gray-200 font-light mb-8 leading-relaxed">
                  Realizza il tuo progetto di outdoor living senza pensieri. 
                  Paga il 50% all'ordine e il resto in comode rate annuali, 
                  <strong className="text-white"> senza interessi</strong>.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <a href="#come-funziona">
                    <Button className="bg-sky-500 hover:bg-sky-400 text-white px-8 py-5 text-lg rounded-full shadow-xl shadow-sky-500/30 transition-all hover:scale-105">
                      Scopri come funziona
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </a>
                  <a href="#richiedi">
                    <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 px-8 py-5 text-lg rounded-full">
                      Richiedi informazioni
                    </Button>
                  </a>
                </div>
              </div>
            </FadeInOnScroll>
            
            {/* Hero Card */}
            <FadeInOnScroll delay={200}>
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl blur opacity-40" />
                <div className="relative bg-white rounded-2xl p-8 shadow-2xl">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-100 rounded-full mb-4">
                      <Percent className="w-8 h-8 text-sky-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">TAN 0% | TAEG 0%</h3>
                    <p className="text-gray-500 mt-1">Nessun interesse, nessuna sorpresa</p>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                      <Euro className="w-6 h-6 text-sky-500" />
                      <span className="text-gray-700 text-lg">Acconto <strong>50%</strong> all'ordine</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                      <Calendar className="w-6 h-6 text-sky-500" />
                      <span className="text-gray-700 text-lg"><strong>10 rate annuali</strong> per il saldo</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                      <Clock className="w-6 h-6 text-sky-500" />
                      <span className="text-gray-700 text-lg">Prima rata dopo <strong>30 giorni</strong></span>
                    </div>
                  </div>
                  
                  <div className="text-center pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">Per beni legati all'efficientamento energetico</p>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Key Benefits Bar */}
      <section className="bg-sky-500 py-6">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-white text-center">
            <div className="flex items-center justify-center gap-3">
              <Percent className="w-6 h-6" />
              <span className="font-medium">TAN e TAEG 0%</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Calendar className="w-6 h-6" />
              <span className="font-medium">10 rate annuali</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Shield className="w-6 h-6" />
              <span className="font-medium">Nessun costo nascosto</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <CheckCircle className="w-6 h-6" />
              <span className="font-medium">Approvazione rapida</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="come-funziona" className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="text-center mb-16">
              <p className="text-sky-500 font-medium mb-4 tracking-wide">
                SEMPLICE E TRASPARENTE
              </p>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
                Come funziona il finanziamento
              </h2>
              <p className="text-gray-600 font-light max-w-2xl mx-auto">
                Un modo intelligente per realizzare il tuo progetto, con rate leggere e senza interessi
              </p>
            </div>
          </FadeInOnScroll>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
            {[
              { 
                step: "01", 
                title: "Scegli il prodotto", 
                desc: "Pergotenda, bioclimatica, tenda da sole: scegli la soluzione perfetta per il tuo spazio."
              },
              { 
                step: "02", 
                title: "Versa l'acconto", 
                desc: "Paghi solo il 50% all'ordine. Il resto lo finanzi a tasso zero."
              },
              { 
                step: "03", 
                title: "Paga comodamente", 
                desc: "10 rate annuali leggere, senza interessi. Una rata all'anno, nessuna sorpresa."
              }
            ].map((item, index) => (
              <FadeInOnScroll key={index} delay={index * 150}>
                <div className="relative text-center p-8">
                  <div className="text-6xl font-bold text-sky-100 mb-4">{item.step}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 font-light">{item.desc}</p>
                  {index < 2 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ChevronRight className="w-8 h-8 text-sky-300" />
                    </div>
                  )}
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Example Calculation */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeInOnScroll>
              <div>
                <p className="text-sky-500 font-medium mb-4 tracking-wide">
                  UN ESEMPIO CONCRETO
                </p>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                  Vediamo i numeri
                </h2>
                <p className="text-gray-600 font-light leading-relaxed mb-6">
                  Immagina di acquistare una pergola bioclimatica da <strong>€10.000</strong>. 
                  Con il nostro finanziamento a tasso zero, ecco come funziona il pagamento:
                </p>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Nessun interesse aggiuntivo sull'importo finanziato</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Rate annuali = budget più facile da gestire</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Ideale per prodotti che aumentano l'efficienza energetica</span>
                  </li>
                </ul>
                
                <a href="#richiedi">
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-3 rounded-none">
                    Richiedi il tuo finanziamento
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={200}>
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-6">
                  <h3 className="text-xl font-medium">Esempio: Pergola Bioclimatica</h3>
                  <p className="text-gray-400 text-sm mt-1">Finanziamento a Tasso Zero</p>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-center py-4 border-b border-gray-100">
                    <span className="text-gray-600">Prezzo totale</span>
                    <span className="text-2xl font-semibold">€ 10.000</span>
                  </div>
                  <div className="flex justify-between items-center py-4 border-b border-gray-100">
                    <span className="text-gray-600">Acconto (50%)</span>
                    <span className="text-xl font-semibold text-gray-900">€ 5.000</span>
                  </div>
                  <div className="flex justify-between items-center py-4 border-b border-gray-100">
                    <span className="text-gray-600">Importo finanziato</span>
                    <span className="text-xl font-semibold text-sky-500">€ 5.000</span>
                  </div>
                  <div className="bg-sky-50 -mx-6 px-6 py-5">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-gray-900 font-medium block">Rata annuale</span>
                        <span className="text-gray-500 text-sm">per 10 anni</span>
                      </div>
                      <span className="text-3xl font-bold text-sky-500">€ 500</span>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 px-6 py-4 border-t border-green-100">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Totale dovuto: € 5.000 (TAEG 0%)</span>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Parallax Image Section 1 */}
      <section className="relative h-[50vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/lklbf91x_biocliamtica%20vetrate.png)`
          }}
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative h-full flex items-center justify-center">
          <FadeInOnScroll>
            <div className="text-center text-white px-6">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-4">
                Uno spazio da vivere tutto l'anno
              </h2>
              <p className="text-xl text-white/80 font-light max-w-2xl mx-auto">
                Pergole bioclimatiche, pergotende e vetrate: tutti finanziabili a tasso zero
              </p>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Ci Pensiamo Noi Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-sky-500 to-blue-600 text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeInOnScroll>
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">Zero Burocrazia</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-6 leading-tight">
                  Alla documentazione<br />
                  <span className="font-semibold">ci pensiamo noi</span>
                </h2>
                <p className="text-xl text-white/90 font-light leading-relaxed mb-8">
                  Non dovrai preoccuparti di nulla. Il nostro team si occupa di tutta la pratica 
                  di finanziamento: dalla raccolta documenti alla presentazione della richiesta, 
                  fino all'approvazione finale.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <span className="text-lg">Raccolta e verifica documenti</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <span className="text-lg">Compilazione della pratica</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <span className="text-lg">Invio e monitoraggio approvazione</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <span className="text-lg">Assistenza fino alla conferma</span>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={200}>
              <div className="bg-white rounded-2xl p-8 text-gray-900">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-sky-100 rounded-full mb-4">
                    <FileText className="w-10 h-10 text-sky-500" />
                  </div>
                  <h3 className="text-2xl font-semibold">Servizio Chiavi in Mano</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <span className="text-2xl font-bold text-sky-500">1</span>
                    <span className="text-gray-700">Scegli il prodotto e il finanziamento</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <span className="text-2xl font-bold text-sky-500">2</span>
                    <span className="text-gray-700">Fornisci i documenti richiesti</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <span className="text-2xl font-bold text-sky-500">3</span>
                    <span className="text-gray-700">Noi gestiamo tutta la pratica</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                    <span className="text-2xl font-bold text-green-500">✓</span>
                    <span className="text-gray-700 font-medium">Finanziamento approvato!</span>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="text-center mb-12">
              <p className="text-sky-500 font-medium mb-4 tracking-wide">
                PRODOTTI FINANZIABILI
              </p>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
                Scopri tutti i prodotti
              </h2>
              <p className="text-gray-600 font-light max-w-2xl mx-auto">
                Il finanziamento è pensato per beni legati all'efficientamento energetico e al risparmio
              </p>
            </div>
          </FadeInOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <Link to={product.link}>
                  <div className="group bg-gray-50 hover:bg-sky-500 p-8 rounded-xl transition-all duration-300 text-center h-full">
                    <product.icon className="w-12 h-12 mx-auto mb-4 text-sky-500 group-hover:text-white transition-colors" />
                    <h3 className="text-lg font-medium text-gray-900 group-hover:text-white transition-colors">
                      {product.name}
                    </h3>
                    <ChevronRight className="w-5 h-5 mx-auto mt-4 text-gray-400 group-hover:text-white transition-colors" />
                  </div>
                </Link>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-20 lg:py-28 bg-gray-900 text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-light mb-4">
                Perché scegliere Giovanni Tende
              </h2>
              <p className="text-gray-400 font-light max-w-2xl mx-auto">
                Ti accompagniamo dall'idea alla realizzazione, con soluzioni di pagamento su misura
              </p>
            </div>
          </FadeInOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "Dal 1967", desc: "Oltre 55 anni di esperienza nel settore outdoor" },
              { title: "Produzione propria", desc: "Realizziamo tutto in Toscana, con materiali certificati" },
              { title: "Installazione inclusa", desc: "Team interno di tecnici professionisti" },
              { title: "Assistenza dedicata", desc: "Ti seguiamo in ogni fase, dal preventivo al post-vendita" }
            ].map((item, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div className="text-center">
                  <CheckCircle className="w-10 h-10 mx-auto mb-4 text-sky-400" />
                  <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                  <p className="text-gray-400 font-light text-sm">{item.desc}</p>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Parallax Image Section 2 */}
      <section className="relative h-[50vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/ddj00xrj_fotobionottetoscana.jpeg)`
          }}
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative h-full flex items-center justify-center">
          <FadeInOnScroll>
            <div className="text-center text-white px-6">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-4">
                Dalla Toscana con passione
              </h2>
              <p className="text-xl text-white/80 font-light max-w-2xl mx-auto">
                Ogni installazione racconta una storia di qualità e artigianalità
              </p>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="richiedi" className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <FadeInOnScroll>
              <div>
                <p className="text-sky-500 font-medium mb-4 tracking-wide">
                  RICHIEDI INFORMAZIONI
                </p>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                  Vuoi saperne di più?
                </h2>
                <p className="text-gray-600 font-light leading-relaxed mb-8">
                  Compila il form e un nostro consulente ti contatterà per spiegarti 
                  nel dettaglio come funziona il finanziamento a tasso zero e come 
                  richiederlo per il tuo progetto.
                </p>
                
                <div className="bg-sky-50 p-6 rounded-xl mb-8">
                  <h4 className="font-medium text-gray-900 mb-3">Cosa ti serve sapere:</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-sky-500" />
                      Finanziamento soggetto ad approvazione
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-sky-500" />
                      Prima rata a 30 giorni dalla stipula
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-sky-500" />
                      Offerta valida fino al 31/12/2026
                    </li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-sky-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Telefono</p>
                      <p className="font-medium text-gray-900">050 878189</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-sky-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">tappezzeria.giovanni@gmail.com</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>

            <FadeInOnScroll delay={200}>
              <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome e Cognome *
                    </label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full rounded-none border-gray-300"
                      placeholder="Il tuo nome"
                      data-testid="smart-form-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefono *
                    </label>
                    <Input
                      required
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full rounded-none border-gray-300"
                      placeholder="Il tuo numero"
                      data-testid="smart-form-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <Input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full rounded-none border-gray-300"
                      placeholder="La tua email"
                      data-testid="smart-form-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Messaggio
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full rounded-none border-gray-300 min-h-[120px]"
                      placeholder="Dimmi di più sul tuo progetto..."
                      data-testid="smart-form-message"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-sky-500 hover:bg-sky-600 text-white py-4 rounded-none text-lg"
                    data-testid="smart-form-submit"
                  >
                    {isSubmitting ? 'Invio in corso...' : 'Richiedi informazioni'}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    * Campi obbligatori. I tuoi dati saranno trattati nel rispetto della privacy.
                  </p>
                </div>
              </form>
            </FadeInOnScroll>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SmartPage;
